import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, Spinner } from "react-bootstrap";
import Select from "react-select";
import PropTypes from "prop-types";
import { getDoctorsAction } from "../../../../../store/actions/DoctorsAction";
import {
  getNotesAction,
  createNoteAction,
} from "../../../../../store/actions/NotesAction";
import { getNotes } from "../../../../../store/selectors/NoteSelector";
import { formatDate } from "../../../../../utils/dateUtils";

const ServiceCallerNotes = ({ defaultDoctorId, patientId }) => {
  const dispatch = useDispatch();
  const doctors = useSelector((state) => state.doctors.doctors || []);
  const notes = useSelector(getNotes);
  const [showModal, setShowModal] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [isImportant, setIsImportant] = useState(false);
  const [isEmergency, setIsEmergency] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState(["4", "5"]); // Default roles as per request body
  const [loading, setLoading] = useState(false);
  const [activeMic, setActiveMic] = useState(null); // "note" | null
  const [speechLang, setSpeechLang] = useState("en-IN");
  const recognitionRef = useRef(null);

  const toggleSpeechLang = () => {
    setSpeechLang((prev) => (prev === "en-IN" ? "mr-IN" : "en-IN"));
  };

  const startListening = (key) => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Speech recognition not supported");
      return;
    }

    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;

    recognition.lang = speechLang;
    recognition.continuous = false;
    recognition.interimResults = false;

    setActiveMic(key);

    recognition.onresult = (event) => {
      const text = event.results[0][0].transcript;

      if (key === "note") {
        setNoteText((prev) => (prev ? prev + " " + text : text));
      }
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      setActiveMic(null);
    };

    recognition.onerror = () => {
      recognitionRef.current = null;
      setActiveMic(null);
    };

    recognition.start();
  };

  const handleShow = () => setShowModal(true);
  const handleClose = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
  
    setActiveMic(false);
    setShowModal(false);
  }
  const handleDoctorChange = (selectedOption) => {
    setSelectedDoctorId(selectedOption?.value || null);
  };

  const selectedDoctor = doctors.find(
    (doc) => doc.doctor_id === selectedDoctorId
  );

  const selectedDoctorValue = selectedDoctor
    ? {
      value: selectedDoctor.doctor_id,
      label: selectedDoctor.doctor_name,
      specialization: selectedDoctor.specialization,
    }
    : null;

  const formatOptionLabel = ({ label, specialization }) => (
    <div>
      <div style={{ fontWeight: "bold" }}>{label}</div>
      <div style={{ fontSize: "0.8em", color: "#666" }}>{specialization}</div>
    </div>
  );

  useEffect(() => {
    dispatch(getDoctorsAction());
    if (patientId) {
      dispatch(getNotesAction(patientId));
    }
  }, [dispatch, patientId]);

  useEffect(() => {
    if (defaultDoctorId) {
      setSelectedDoctorId(defaultDoctorId);
    } else {
      setSelectedDoctorId(null);
    }
  }, [defaultDoctorId]);

  const customStyles = {
    option: (provided) => ({
      ...provided,
      display: "flex",
      flexDirection: "column",
      padding: "10px",
    }),
    singleValue: (provided) => ({
      ...provided,
      display: "flex",
      flexDirection: "column",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      position: "absolute",
    }),
    control: (provided) => ({
      ...provided,
      zIndex: 999,
    }),
    container: (provided) => ({
      ...provided,
      position: "relative",
      zIndex: 1000,
    }),
  };
  const admin_name = JSON.parse(localStorage.getItem("loginDetails"))?.id || 1;

  // const handleAddNote = () => {
  //   const noteData = {
  //     patient_id: patientId,
  //     note_description: noteText,
  //     note_type: "Doctor",
  //     sub_role: JSON.stringify([""]),
  //     prescription_id: 0,
  //     is_important: isImportant ? "Yes" : "No",
  //     is_emergency: isEmergency ? "Yes" : "No",
  //     doctor_id: selectedDoctorId,
  //     created_by_name: admin_name || "Unknown User",
  //   };

  //   dispatch(createNoteAction(noteData));
  //   setNoteText("");
  //   setIsImportant(false);
  //   setIsEmergency(false);
  //   setShowModal(false);
  // };
  const handleAddNote = async () => {
    if (loading) return;
    setLoading(true);
    const noteData = {
      patient_id: patientId,
      note_description: noteText,
      note_type: "Doctor",
      sub_role: JSON.stringify([""]),
      prescription_id: 0,
      is_important: isImportant ? "Yes" : "No",
      is_emergency: isEmergency ? "Yes" : "No",
      doctor_id: selectedDoctorId,
      created_by_name: admin_name || "Unknown User",
    };

    try {
      await dispatch(createNoteAction(noteData));
      await dispatch(getNotesAction(patientId));
    } catch (err) {
      console.error("Failed to add note:", err);
    } finally {
      setLoading(false);
    }

    setNoteText("");
    setIsImportant(false);
    setIsEmergency(false);
    setShowModal(false);
  };



  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="card shadow-none">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="card-title mb-0">Patient Notes</h4>
          <Button variant="outline-primary" size="sm"onClick={handleShow}>
            <i className="fas fa-plus me-2"></i>Add Note
          </Button>
        </div>


        <div className="mt-3">
          {notes && notes.length > 0 ? (
            notes.map((note) => (
              <div
                key={note.note_id}
                className="mb-3 custom-timeline-panel timeline-panel bgl-dark border-0 p-3 rounded shadow-sm position-relative"
              >
                {/* Badges - positioned in the top-right */}
                <div className="position-absolute" style={{ top: "10px", right: "10px", display: "flex", gap: "6px" }}>
                  {note.is_important === "Yes" && (
                    <span
                      className="badge"
                      style={{
                        background: "#fff4de",
                        color: "#ffa755",
                        padding: "6px 12px",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: "500",
                      }}
                    >
                      Important
                    </span>
                  )}
                  {note.is_emergency === "Yes" && (
                    <span
                      className="badge"
                      style={{
                        background: "#ffe2e5",
                        color: "#ff4c41",
                        padding: "6px 12px",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: "500",
                      }}
                    >
                      Emergency
                    </span>
                  )}
                </div>

                {/* Main content aligned left */}
                <p className="mb-1" style={{ fontSize: "14px", color: "#444" }}>
                  {note.note_description}
                </p>

                <p className="mb-1" style={{ fontSize: "14px", color: "#666" }}>
                  <strong>Date:</strong> {formatDate(note.created_at)}
                </p>

                {/* <div style={{ fontSize: "14px", color: "#2b2b2b" }}>
                  <strong>Dr. {note.created_by_name || "-"}</strong>: {note.doctor_reply || "-"} (
                  <span style={{ color: "#666" }}>
                    {formatDate(note.doctor_reply_date)} at {formatTime(note.doctor_reply_date)}
                  </span>
                  )
                </div> */}
                {note.doctor_reply?.trim() && (
                  <div style={{ fontSize: "14px", color: "#2b2b2b" }}>
                    <strong>Dr. {note.created_by_name || "-"}</strong>: {note.doctor_reply} (
                    <span style={{ color: "#666" }}>
                      {formatDate(note.doctor_reply_date)} at {formatTime(note.doctor_reply_date)}
                    </span>
                    )
                  </div>
                )}

              </div>
            ))
          ) : (
            <div
              className="text-center py-5"
              style={{
                background: "#f8f9fa",
                borderRadius: "10px",
                margin: "20px 0",
              }}
            >
              <i
                className="fas fa-clipboard-list"
                style={{
                  fontSize: "48px",
                  color: "#d1d3e2",
                  marginBottom: "15px",
                }}
              ></i>
              <p className="text-muted mb-0">No notes available</p>
            </div>
          )}
        </div>
        {/* Modal */}
        <Modal show={showModal} onHide={handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>Add Notes</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3 position-relative">
              <label className="form-label">Note</label>
              <textarea
                className="form-control pe-5"
                rows="3"
                value={noteText}
                placeholder="Enter the Notes here"
                onChange={(e) => setNoteText(e.target.value)}
              />

              {/* LANG toggle */}
              <button
                type="button"
                onClick={toggleSpeechLang}
                style={{
                  position: "absolute",
                  right: "44px",
                  top: "80px",
                  padding: "2px 6px",
                  borderRadius: "10px",
                  fontSize: "11px",
                  border: "1px solid #ccc",
                  background: "#fff",
                }}
              >
                {speechLang === "en-IN" ? "EN" : "MR"}
              </button>

              {/* MIC */}
              <button
                type="button"
                onClick={() => startListening("note")}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "75px",
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  border: "none",
                  background: activeMic === "note" ? "#ff4d4d" : "#f1f3f5",
                  boxShadow: activeMic === "note" ? "0 0 8px rgba(255,0,0,0.6)" : "0 2px 4px rgba(0,0,0,0.2)",
                }}
              >
                <i
                  className="fas fa-microphone"
                  style={{ color: activeMic === "note" ? "white" : "#333", fontSize: "13px" }}
                />
              </button>
            </div>

            <div className="form-check d-flex align-items-center mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="importantCheck"
                checked={isImportant}
                onChange={(e) => setIsImportant(e.target.checked)}
              />
              <label className="form-check-label ms-2" htmlFor="importantCheck">
                Important
              </label>
            </div>
            <div className="form-check d-flex align-items-center mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="emergencyCheck"
                checked={isEmergency}
                onChange={(e) => setIsEmergency(e.target.checked)}
              />
              <label className="form-check-label ms-2" htmlFor="emergencyCheck">
                Emergency
              </label>
            </div>

            <div className="row mb-4">
              <div
                className="col-md-12"
                style={{ position: "relative", zIndex: 1000 }}
              >
                <label className="form-label">Select Doctor</label>
                <Select
                  value={selectedDoctorValue}
                  onChange={handleDoctorChange}
                  options={doctors.map((doctor) => ({
                    value: doctor.doctor_id,
                    label: doctor.doctor_name,
                    specialization: doctor.specialization,
                  }))}
                  formatOptionLabel={formatOptionLabel}
                  styles={customStyles}
                  isClearable={true}
                  isSearchable
                  placeholder="Select a doctor"
                  className="basic-single"
                  classNamePrefix="select"
                  noOptionsMessage={() => "No doctors found"}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-end">
            {/* <Button variant="primary" onClick={handleAddNote}>
              Add
            </Button> */
            }
            <Button variant="primary" onClick={handleAddNote} disabled={loading || !noteText.trim()}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Adding...
                </>
              ) : (
                "Add"
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

ServiceCallerNotes.propTypes = {
  defaultDoctorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default ServiceCallerNotes;
